import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Heading, Subheading } from "../../design/heading";
import { Text } from "../../design/text";
import { Button } from "../../design/button";
import { Input } from "../../design/input";
import { Divider } from "../../design/divider";
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
  ErrorMessage,
} from "../../design/fieldset";
import { UserProfile } from "@visitproof/types";
import { UserService } from "../../services/UserService";
import { storage } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  CameraIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Avatar } from "../../design/avatar";
import { getInitials } from "../../utils/string";
import { Badge } from "../../design/badge";

interface Errors {
  firstName?: string;
  lastName?: string;
}

/**
 * Shared profile page for all roles (admin, supervisor, parent)
 */
const RoleBasedProfile: React.FC = () => {
  const { user, role, isAdmin, isMonitor, isParent } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [, setCurrentUser] = useState<UserProfile | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadUser = useCallback(async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);
      const userData = await UserService.getUser(user.uid);
      if (userData) {
        setCurrentUser(userData);
        setFirstName(userData.firstName || "");
        setLastName(userData.lastName || "");
        setAvatarUrl(userData.avatarUrl || null);
      }
    } catch (err) {
      console.error("Error loading user:", err);
      setErrorMessage("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.uid) return;

    try {
      setUploadingImage(true);
      setErrorMessage(null);

      const fileRef = ref(storage, `users/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);

      // Update user with new avatar
      await UserService.updateUser(user.uid, { avatarUrl: downloadUrl });
      setAvatarUrl(downloadUrl);
      setSuccessMessage("Profile picture updated");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error uploading image:", err);
      setErrorMessage("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !user?.uid) return;

    try {
      setSaving(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      await UserService.updateUser(user.uid, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      setSuccessMessage("Profile updated successfully");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Error updating profile:", err);
      setErrorMessage("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = () => {
    if (isAdmin) {
      return <Badge color="purple">Administrator</Badge>;
    }
    if (isMonitor) {
      return <Badge color="blue">Monitor</Badge>;
    }
    if (isParent) {
      return <Badge color="green">Parent</Badge>;
    }
    return <Badge color="zinc">{role}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Text>Loading...</Text>
      </div>
    );
  }

  return (
    <div className="max-w-2xl p-6 mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Heading>My Profile</Heading>
          {getRoleBadge()}
        </div>
        <Text className="text-zinc-600 dark:text-zinc-400">
          Manage your personal information
        </Text>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="flex items-center gap-3 p-4 mb-6 border border-green-200 rounded-xl bg-green-50 dark:border-green-800 dark:bg-green-900/20">
          <CheckCircleIcon className="w-5 h-5 text-green-500 shrink-0" />
          <Text className="text-green-700 dark:text-green-400">{successMessage}</Text>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="flex items-center gap-3 p-4 mb-6 border border-red-200 rounded-xl bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 shrink-0" />
          <Text className="text-red-700 dark:text-red-400">{errorMessage}</Text>
        </div>
      )}

      {/* Profile Picture Section */}
      <div className="p-6 mb-6 bg-white border rounded-xl border-zinc-200 dark:border-zinc-700 dark:bg-zinc-900">
        <Subheading className="mb-4">Profile Picture</Subheading>
        <Divider className="mb-6" />

        <div className="flex items-center gap-6">
          <div className="relative">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Profile"
                className="object-cover w-24 h-24 border-2 rounded-full border-zinc-200 dark:border-zinc-700"
              />
            ) : (
              <Avatar
                className="size-24"
                initials={getInitials(`${firstName} ${lastName}` || user?.displayName || undefined)}
              />
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="absolute bottom-0 right-0 flex items-center justify-center w-8 h-8 transition-colors bg-blue-600 border-2 border-white rounded-full dark:border-zinc-900 hover:bg-blue-700 disabled:opacity-50"
            >
              {uploadingImage ? (
                <svg className="w-4 h-4 text-white animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <CameraIcon className="w-4 h-4 text-white" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
          </div>

          <div>
            <Text className="font-medium text-zinc-900 dark:text-zinc-100">
              {firstName} {lastName}
            </Text>
            <Text className="text-sm text-zinc-500 dark:text-zinc-400">
              {user?.email || user?.phoneNumber}
            </Text>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
            >
              {uploadingImage ? "Uploading..." : "Change photo"}
            </button>
          </div>
        </div>
      </div>

      {/* Personal Information Form */}
      <div className="p-6 bg-white border rounded-xl border-zinc-200 dark:border-zinc-700 dark:bg-zinc-900">
        <Subheading className="mb-4">Personal Information</Subheading>
        <Divider className="mb-6" />

        <form onSubmit={handleSubmit}>
          <Fieldset>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field>
                  <Label>First Name</Label>
                  <Input
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (errors.firstName) {
                        setErrors({ ...errors, firstName: undefined });
                      }
                    }}
                    placeholder="First Name"
                    invalid={!!errors.firstName}
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </Field>

                <Field>
                  <Label>Last Name</Label>
                  <Input
                    type="text"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      if (errors.lastName) {
                        setErrors({ ...errors, lastName: undefined });
                      }
                    }}
                    placeholder="Last Name"
                    invalid={!!errors.lastName}
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>

            <FieldGroup>
              <Field>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={user?.email || ""}
                  disabled
                  className="bg-zinc-50 dark:bg-zinc-800"
                />
                <Text className="mt-1 text-xs text-zinc-500">
                  Contact support to change your email address
                </Text>
              </Field>
            </FieldGroup>

            {user?.phoneNumber && (
              <FieldGroup>
                <Field>
                  <Label>Phone Number</Label>
                  <Input
                    type="tel"
                    value={user.phoneNumber}
                    disabled
                    className="bg-zinc-50 dark:bg-zinc-800"
                  />
                </Field>
              </FieldGroup>
            )}
          </Fieldset>

          <div className="flex justify-end mt-6">
            <Button type="submit" color="blue" disabled={saving}>
              {saving ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </span>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleBasedProfile;
